import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CalendarMonth,
  Phone,
  Email,
  Launch,
  ContactMail,
  ExpandMore,
  Info,
  OpenInNew
} from '@mui/icons-material';
import { configService } from '../services/configService';

interface Tour {
  name: string;
  bookingUrl: string;
  price: string;
  duration: string;
  keywords: string[];
  aliases: string[];
  category: string;
  priority: number;
}

interface BookingConfig {
  enabled: boolean;
  baseUrl: string;
  modalTarget?: string;
  tours: { [key: string]: Tour };
  fallback: {
    phone: string;
    email: string;
    generalBookingUrl: string;
  };
  buttonStyle: {
    primary: string;
    secondary: string;
    text: string;
  };
}

const CTAButtonsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingConfig, setBookingConfig] = useState<BookingConfig | null>(null);

  useEffect(() => {
    loadBookingConfig();
  }, []);

  const loadBookingConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const config = await configService.getConfig();
      if (config.booking) {
        setBookingConfig(config.booking as BookingConfig);
      } else {
        setError('No booking configuration found');
      }
    } catch (err) {
      console.error('Error loading booking config:', err);
      setError('Failed to load booking configuration');
    } finally {
      setLoading(false);
    }
  };

  const getButtonIcon = (type: string) => {
    switch (type) {
      case 'book':
        return <CalendarMonth fontSize="small" />;
      case 'call':
        return <Phone fontSize="small" />;
      case 'email':
        return <Email fontSize="small" />;
      case 'link':
        return <Launch fontSize="small" />;
      case 'quote':
        return <ContactMail fontSize="small" />;
      default:
        return <Launch fontSize="small" />;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'easy': 'success',
      'intermediate': 'warning', 
      'full-day': 'primary',
      'private': 'secondary',
      'advanced': 'error',
      'water': 'info'
    } as const;
    return colors[category as keyof typeof colors] || 'default';
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return 'error'; // High priority
    if (priority <= 4) return 'warning'; // Medium priority
    return 'success'; // Low priority
  };

  const sortedTours = bookingConfig && bookingConfig.tours ? 
    Object.entries(bookingConfig.tours).sort(([,a], [,b]) => a.priority - b.priority) : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadBookingConfig}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!bookingConfig) {
    return (
      <Alert severity="info">
        No booking configuration available
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        CTA Booking Buttons & Context
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This page shows all the booking buttons and contact options that the AI can display, 
        along with the contexts and keywords that trigger each button.
      </Typography>

      {/* Booking Status */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Booking Configuration Status
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label={bookingConfig.enabled ? 'Enabled' : 'Disabled'}
            color={bookingConfig.enabled ? 'success' : 'error'}
            variant="filled"
          />
          <Typography variant="body2">
            Base URL: <strong>{bookingConfig.baseUrl}</strong>
          </Typography>
        </Box>
      </Paper>

      {/* Tour Booking Buttons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Tour Booking Buttons
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These buttons appear when the AI mentions specific tours or activities. 
          Sorted by priority (most important first).
        </Typography>

        {sortedTours.length === 0 ? (
          <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
            No tours configured. Tours are managed from the backend configuration.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {sortedTours.map(([tourId, tour]) => (
            <Grid item xs={12} md={6} lg={4} key={tourId}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      {tour.name}
                    </Typography>
                    <Chip 
                      label={`Priority ${tour.priority}`}
                      size="small"
                      color={getPriorityColor(tour.priority)}
                      variant="outlined"
                    />
                  </Box>

                  <Box mb={2}>
                    <Chip 
                      label={tour.category}
                      size="small"
                      color={getCategoryColor(tour.category)}
                      sx={{ mr: 1, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {tour.price} • {tour.duration}
                    </Typography>
                  </Box>

                  <Accordion variant="outlined" sx={{ mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2" fontWeight={500}>
                        Trigger Context
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" gutterBottom>
                        Keywords that trigger this button:
                      </Typography>
                      <Box mb={2}>
                        {tour.keywords.map((keyword) => (
                          <Chip
                            key={keyword}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Alternative names (aliases):
                      </Typography>
                      <Box>
                        {tour.aliases.map((alias) => (
                          <Chip
                            key={alias}
                            label={alias}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={getButtonIcon('book')}
                    endIcon={<OpenInNew fontSize="small" />}
                    fullWidth
                    onClick={() => window.open(tour.bookingUrl, '_blank')}
                    sx={{ 
                      backgroundColor: bookingConfig.buttonStyle.primary,
                      '&:hover': {
                        backgroundColor: bookingConfig.buttonStyle.secondary
                      }
                    }}
                  >
                    Book {tour.name}
                  </Button>
                </CardContent>
              </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Fallback Contact Options */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Fallback Contact Options
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These buttons appear when the AI detects booking intent but can't match specific tours, 
          or when users ask general contact questions.
        </Typography>

        <Grid container spacing={2}>
          {/* Call Button */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Call Button</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  <strong>When shown:</strong> General booking questions, urgent inquiries, 
                  accessibility needs, complex requests
                </Typography>
                <Typography variant="body2" mb={2}>
                  <strong>Phone:</strong> {bookingConfig.fallback.phone}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Phone />}
                  fullWidth
                  onClick={() => window.open(`tel:${bookingConfig.fallback.phone}`, '_self')}
                >
                  Call Us
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Email Button */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Email Button</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  <strong>When shown:</strong> Detailed inquiries, group bookings, 
                  special requests, non-urgent questions
                </Typography>
                <Typography variant="body2" mb={2}>
                  <strong>Email:</strong> {bookingConfig.fallback.email}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  fullWidth
                  onClick={() => window.open(`mailto:${bookingConfig.fallback.email}`, '_self')}
                >
                  Email Us
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Page Button */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ContactMail color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Contact Page</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  <strong>When shown:</strong> General contact requests, 
                  location information, business hours questions
                </Typography>
                <Typography variant="body2" mb={2}>
                  <strong>URL:</strong> {bookingConfig.fallback.generalBookingUrl}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ContactMail />}
                  endIcon={<OpenInNew fontSize="small" />}
                  fullWidth
                  onClick={() => window.open(bookingConfig.fallback.generalBookingUrl, '_blank')}
                >
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* AI Detection Logic */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          AI Button Detection Logic
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Info color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Keyword Matching"
              secondary="The AI scans user messages and bot responses for tour keywords and aliases to determine which booking buttons to show."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Info color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Priority System"
              secondary="When multiple tours are detected, buttons are shown based on priority (1 = highest). The most relevant tours appear first."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Info color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Fallback Logic"
              secondary="If no specific tours are detected but booking intent is identified, fallback contact options (call, email, contact page) are shown."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Info color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Context Awareness"
              secondary="The system considers conversation context, user questions, and bot responses to determine the most appropriate buttons to display."
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default CTAButtonsPage;
